/**
 * `#intelHeadlines` — the Overview sub-tab (design `2026-09-28-intel-subtabs-design.md` §1/§3):
 * a stacked bar of watersheds by category, four stat tiles (High Priority watersheds,
 * agricultural area in them, most common constraint, reference year) and, when any
 * country row exists, the same stacked bar for countries. Read off `{uc}/summary`'s own
 * `summary` object, whose shape is the use case's, so this component is UC1-shaped for now
 * (Task 7's UC2/UC3 headlines are their own reading of the same envelope, not an extension
 * of this one).
 *
 * Prose headline sentences and main crops are gone from this panel — main crops move to
 * Key indicators (a per-unit fact), and limitations move to Methodology.
 */
import { useTranslation } from 'react-i18next';

import { ChartFrame } from '../../components/charts/ChartFrame';
import type { IntelSummary } from '../../lib/api';
import { fmtCompactNum, fmtNum } from '../../lib/format';
import { StackedBar, type StackedBarSegment } from './charts/StackedBar';
import { StatTile, StatTiles } from './charts/StatTile';
import { CATEGORY_COLORS, REFERENCE_YEAR, UC1_CATEGORIES as CATEGORIES } from './uc1Constants';

interface Uc1SummaryBody {
  by_category?: Record<string, Record<string, number>>;
  ag_area_ha_high?: number;
  ag_area_share_high?: number;
  main_crops?: string[];
  most_common_constraint?: string;
}

export function Headlines({ summary }: { summary: IntelSummary }) {
  const { t } = useTranslation();
  const body = summary.summary as Uc1SummaryBody;
  const byWatershed = body.by_category?.watershed ?? {};
  const byCountry = body.by_category?.admin0 ?? {};
  const countryTotal = CATEGORIES.reduce((sum, cat) => sum + (byCountry[cat] ?? 0), 0);

  const segments = (counts: Record<string, number>): StackedBarSegment[] =>
    CATEGORIES.map((cat) => ({
      id: cat,
      label: t(`intel.uc1.category.${cat}`),
      value: counts[cat] ?? 0,
      color: CATEGORY_COLORS[cat]!,
    }));

  // `most_common_constraint` is one of the five constraint ids; an id this build does not
  // know (a payload from an older run) prints as itself rather than as a missing key.
  const constraintLabel = body.most_common_constraint
    ? t(`intel.uc1.dimension.${body.most_common_constraint}`, {
        defaultValue: body.most_common_constraint,
      })
    : null;

  // The unit is only honest next to a number: an absent hectare figure prints the em dash
  // on its own, not "— ha".
  const agHaHigh = typeof body.ag_area_ha_high === 'number' ? body.ag_area_ha_high : null;

  return (
    <div id="intelHeadlines" className="intel-headlines intel-card">
      <h5 className="intel-card-title">{t('intel.uc1.chart.categories')}</h5>
      <p className="intel-card-sub">{t('intel.uc1.guide.overview')}</p>
      <ChartFrame
        title={t('intel.uc1.chart.categories')}
        subtitle={t('intel.uc1.guide.overview')}
        testId="uc1-categories-chart"
      >
        {() => (
          <StackedBar segments={segments(byWatershed)} ariaLabel={t('intel.uc1.chart.categories')} />
        )}
      </ChartFrame>
      <StatTiles>
        <StatTile
          id="uc1-high-watersheds"
          value={fmtNum(byWatershed.high ?? 0, 0)}
          label={t('intel.uc1.tile.highWatersheds')}
        />
        <StatTile
          id="uc1-ag-area-high"
          value={fmtCompactNum(agHaHigh)}
          unit={agHaHigh != null ? 'ha' : undefined}
          label={t('intel.uc1.tile.agAreaHigh')}
          sub={
            body.ag_area_share_high != null
              ? `${fmtNum(body.ag_area_share_high * 100, 0)}%`
              : undefined
          }
        />
        <StatTile
          id="uc1-constraint"
          value={constraintLabel ?? '-'}
          text
          label={t('intel.uc1.tile.constraint')}
        />
        {/* The reference year of the inputs (2025), never the run's own stamp: a run in
            October 2026 still reads 2025 rainfall. "Last computed" lives in Methodology. */}
        <StatTile
          id="uc1-period"
          value={String(REFERENCE_YEAR)}
          label={t('intel.uc1.tile.period')}
        />
      </StatTiles>
      {countryTotal > 0 ? (
        <>
          <h6 className="intel-block-title">{t('intel.uc1.chart.countries')}</h6>
          <ChartFrame
            title={t('intel.uc1.chart.countries')}
            testId="uc1-countries-chart"
          >
            {() => (
              <StackedBar segments={segments(byCountry)} ariaLabel={t('intel.uc1.chart.countries')} />
            )}
          </ChartFrame>
        </>
      ) : null}
    </div>
  );
}

export default Headlines;
